import type { TimelineRange } from "@/utils/sharedTimeline";

/**
 * A lap as reported by GET /telemetry-data/:sessionId/laps. `startTime` /
 * `endTime` / `lapTime` are telemetry-timeline seconds (the chart x-axis,
 * the same scale as telemetry-note timeOffsets).
 */
export interface SessionLapTiming {
  lapNumber: number;
  /** Start/finish crossing that begins the lap; null when unknown. */
  startTime?: number | null;
  endTime?: number | null;
  /** Lap duration in seconds. */
  lapTime: number;
  isValid?: boolean;
}

export interface SessionLapMarker {
  lap: SessionLapTiming;
  /** Video-timeline seconds of the lap's start/finish crossing. */
  sessionTime: number;
}

/**
 * Map a telemetry lap onto the video session timeline. Telemetry laps are
 * reported in telemetry-timeline seconds, so they convert exactly like
 * telemetry-note timeOffsets: subtract the sync offset. Laps without a
 * startTime (no detectable crossing) have no position.
 */
export const getLapStartSessionTime = (
  lap: SessionLapTiming,
  syncOffset: number,
): number | null => {
  if (lap.startTime === undefined || lap.startTime === null) {
    return null;
  }

  const sessionTime = lap.startTime - syncOffset;
  return Number.isFinite(sessionTime) ? sessionTime : null;
};

/** Placeable lap markers sorted by crossing time, optionally range-filtered. */
export const getLapMarkers = (
  laps: SessionLapTiming[] | null | undefined,
  syncOffset: number,
  range?: TimelineRange | null,
): SessionLapMarker[] => {
  if (!laps || laps.length === 0) {
    return [];
  }

  const markers: SessionLapMarker[] = [];
  for (const lap of laps) {
    const sessionTime = getLapStartSessionTime(lap, syncOffset);
    if (sessionTime === null) {
      continue;
    }
    if (range && (sessionTime < range.start || sessionTime > range.end)) {
      continue;
    }
    markers.push({ lap, sessionTime });
  }
  return markers.sort((left, right) => left.sessionTime - right.sessionTime);
};
